'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { LineInfoCircle } from '@qr-front/icons/line-info-circle'
import { LinePlus } from '@qr-front/icons/line-plus'
import { Button } from '@qr-front/ui/components/Button'
import { RhcField } from 'features/generator/components/RhcField'
import { useSyncFieldWithAtom } from 'hooks/useSyncFieldWithAtom'
import { useAtom, useSetAtom } from 'jotai'
import { RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'
import { FormProvider, Resolver, useForm } from 'react-hook-form'
import { resettableAtomWithStorage } from 'utils/atoms'
import { InferType } from 'yup'

import { transformBackendResponseKey } from '@/config'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'
import { validateContentForm } from '@/utils'

import { GENERATOR_FORM_ANIMATION_DURATION } from '../../components/GeneratorFormSection'
import {
	TContentFormHandle,
	TQrContentFormSection,
} from '../qrContentFormTypes'
import { useSyncFormSectionValidity } from '../useSyncFormSectionValidity'
import {
	ButtonsSection,
	videoButtonListAtom,
} from './ButtonsSection/ButtonsSection'
import { getVideoInfoFormSchema } from './VideoInfoFormModel'

export const VIDEO_INFORMATION_FORM_ID = 'video-information'
export const VIDEO_MAX_LENGTH = 100

export const videoTitleAtom = resettableAtomWithStorage('video-title', '')

export const videoDescriptionAtom = resettableAtomWithStorage(
	'video-description',
	'',
)

export type TVideoFormField = InferType<
	ReturnType<typeof getVideoInfoFormSchema>
>

export const resetVideoInformationForm = () => {
	getStore().set(videoTitleAtom, RESET)
	getStore().set(videoDescriptionAtom, RESET)
	getStore().set(videoButtonListAtom, RESET)
}

export const VideoForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()
	const [videoTitle, setVideoTitle] = useAtom(videoTitleAtom)
	const [videoDescription, setVideoDescription] =
		useAtom(videoDescriptionAtom)

	const form = useForm<TVideoFormField>({
		defaultValues: {
			title: videoTitle,
			description: videoDescription,
		},
		mode: 'onChange',
		reValidateMode: 'onChange',
		resolver: yupResolver(
			getVideoInfoFormSchema(t),
		) as unknown as Resolver<TVideoFormField>,
		shouldUnregister: false,
	})

	const {
		formState: { errors, isValid },
		setValue,
		handleSubmit,
		getValues,
		setFocus,
	} = form

	useSyncFormSectionValidity({ formId: VIDEO_INFORMATION_FORM_ID, isValid })

	useSyncFieldWithAtom({
		atom: videoTitleAtom,
		setFieldValue: (value) => {
			setValue('title', value)
		},
	})

	useSyncFieldWithAtom({
		atom: videoDescriptionAtom,
		setFieldValue: (value) => {
			setValue('description', value)
		},
	})

	useImperativeHandle(
		ref,
		() => ({
			trigger: (property: string, message: string) => {
				const translatedMessage = t(
					transformBackendResponseKey(message),
				)
				property = property.replace('info.buttons.', 'buttons.')
				form.setError(property as Parameters<typeof form.setError>[0], {
					message: translatedMessage,
				})
			},
			validate: () =>
				validateContentForm(
					(onValid, onInvalid) =>
						handleSubmit(onValid, async (errors) => {
							onInvalid(errors)
						}),
					GENERATOR_FORM_ANIMATION_DURATION * 1.25,
				),
		}),
		[handleSubmit],
	)

	const setButtonList = useSetAtom(videoButtonListAtom)

	const handleClickAddButton = () => {
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		setButtonList((prev) => [...prev, { title: '', url: '' }])
		setTimeout(
			() => setFocus(`buttons.${getValues('buttons')!.length - 1}.title`),
			0,
		)
	}

	return (
		<FormProvider {...form}>
			<form className="flex flex-col gap-6">
				<div className="flex flex-col gap-4 tablet:flex-row tablet:gap-12">
					<RhcField
						type="text"
						form={form}
						path="title"
						value={videoTitle}
						setValue={(value) => setVideoTitle(value || '')}
						className="w-full"
						label={t(
							'generator__content_form_section__videos_information__headline__label',
						)}
						placeholder={t(
							'generator__content_form_section__videos_information__headline__placeholder',
						)}
						fieldName="title"
						error={errors.title?.message}
					/>
					<RhcField
						type="text"
						form={form}
						path="description"
						value={videoDescription}
						setValue={(value) => setVideoDescription(value || '')}
						className="w-full"
						label={t(
							'generator__content_form_section__videos_information__description__label',
						)}
						placeholder={t(
							'generator__content_form_section__videos_information__description__placeholder',
						)}
						fieldName="description"
						error={errors.description?.message}
					/>
				</div>
				<ButtonsSection />
				<Button
					visual="soft"
					onPointerDown={handleClickAddButton}
					className="w-fit [&>svg]:size-4"
				>
					<LinePlus />
					{t(
						'generator__content_form_section__videos_information__add_button',
					)}
				</Button>
			</form>
		</FormProvider>
	)
})

VideoForm.displayName = 'VideoForm'

export const videoInformationFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: VIDEO_INFORMATION_FORM_ID,
	title: t('generator__content_form_section__videos_information__title'),
	description: t(
		'generator__content_form_section__videos_information__description',
	),
	icon: <LineInfoCircle />,
	Content: VideoForm,
})
